import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { DeliveryTicket } from "@/pages/caster/models/DeliveryTicket";
import { CatalogMoldSize } from "@/pages/caster/models/catalogs/CatalogMoldSize";
import {
	getDeliveryTicket,
	getMoldSizes,
	getReadProductBatches,
	updateLabTechnician,
	upsertProcessData,
	upsertProductBatch,
} from "@/pages/caster/repositories/ProductionRepository";
import { upsertEAFProcessData } from "@/pages/eaf/eaf-processes/repositories/EAFRepository";
import { upsertHeat } from "@/pages/meltshop/heats/repositories/heatsRepository";
import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import { useMemo, useState } from "react";
import {
	calculateBatchWeight,
	updateBatchesWithWeight,
} from "../helpers/calculateWeight";

const moment = getMomentTz();

export interface ProcessDataChanges {
	gradeId?: number | null;
	moldSizeId?: number | null;
	casterForeman?: string | null;
	melter?: string | null;
	labTech?: string | null;
	sequence?: string | null;
}

export const useDeliveryTicket = (processId: number, show: boolean) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [deliveryTicket, setDeliveryTicket] = useState<DeliveryTicket | null>(
		null,
	);
	const [moldSizes, setMoldSizes] = useState<CatalogMoldSize[] | null>(null);
	const [pendingChanges, setPendingChanges] = useState<ProcessDataChanges>({});
	const [errors, setErrors] = useState<Map<string, string>>(new Map());

	const loadData = async () => {
		setIsLoading(true);
		if (!processId) {
			setError("No process id provided.");
		} else {
			const resp = await getDeliveryTicket(processId);
			if (resp.ok) {
				const [heatTable, productBatchesTable] = resp.data.tables;
				setDeliveryTicket({
					heatData: heatTable.rows[0],
					productBatches: productBatchesTable.rows,
				});
			} else {
				setError(resp.message);
			}
			const moldSizesResp = await getMoldSizes();
			if (moldSizesResp.ok) {
				setMoldSizes(moldSizesResp.data.tables[0].rows);
			} else {
				setError(moldSizesResp.message);
			}
		}
		setIsLoading(false);
	};

	const refreshProductBatches = async () => {
		setIsLoading(true);
		if (!processId) {
			setError("No process id provided.");
		} else {
			if (pendingChanges && Object.keys(pendingChanges).length > 0) {
				await saveAllPendingChanges();
			}
			const productBatchesResponse = await getReadProductBatches(processId);
			if (!productBatchesResponse.ok) {
				setError(productBatchesResponse.message);
			}
		}
		await loadData();
		setIsLoading(false);
	};
	const handleHeatInputChange = (field: string, value: any) => {
		setDeliveryTicket((prevState) => {
			if (!prevState) return null;

			const newHeatData = { ...prevState.heatData, [field]: value };

			if (field === "MoldSizeId") {
				// Verify if there is any product batch that was saved manually
				const hasManualSaved = prevState.productBatches.some(
					(batch) => batch.isManualSaved === true,
				);
				return {
					...prevState,
					heatData: newHeatData,
					productBatches: !hasManualSaved
						? updateBatchesWithWeight(
								prevState.productBatches,
								value,
								moldSizes,
							)
						: prevState.productBatches,
				};
			}

			return { ...prevState, heatData: newHeatData };
		});
	};

	const handleProductBatchChange = (
		index: number,
		key: string,
		value: string,
	) => {
		setDeliveryTicket((prevState) => {
			if (!prevState) return null;
			if (key === "updateAllBatches") {
				return {
					...prevState,
					productBatches: JSON.parse(value),
				};
			}
			const updatedBatches = [...prevState.productBatches];
			if (index >= 0) {
				let updatedBatch = {
					...updatedBatches[index],
					[key]: key === "Length" ? Number(value) : value,
				};

				if (key === "Length" || key === "Pieces") {
					updatedBatch = calculateBatchWeight(
						updatedBatch,
						prevState.heatData.MoldSizeId,
						moldSizes,
					);
				}
				updatedBatches[index] = updatedBatch;
			}
			return { ...prevState, productBatches: updatedBatches };
		});
	};

	const dateTimeFormatted = useMemo(() => {
		let castDateFormatted;
		let tapTime;
		if (deliveryTicket?.heatData.CastDate)
			castDateFormatted = moment
				.utc(deliveryTicket.heatData.CastDate)
				.format("YYYY-MM-DD");
		if (moment.utc(deliveryTicket?.heatData.TapTime).isValid())
			tapTime = dxtToLocalServerTime(
				moment.utc(deliveryTicket?.heatData.TapTime).toDate(),
				"HH:mm",
			);
		else tapTime = deliveryTicket?.heatData.TapTime;
		return { castDateFormatted, tapTime };
	}, [deliveryTicket?.heatData.TapTime, deliveryTicket?.heatData.CastDate]);

	const saveAllPendingChanges = async () => {
		if (!deliveryTicket || !processId) return;
		try {
			const promises = [];

			if (pendingChanges.gradeId !== undefined) {
				promises.push(
					upsertHeat({
						GradeId: pendingChanges.gradeId,
						HeatId: deliveryTicket.heatData.HeatId,
					}),
				);
			}

			if (pendingChanges.moldSizeId !== undefined && processId) {
				promises.push(
					upsertProcessData(processId, "MoldSizeId", pendingChanges.moldSizeId),
				);
			}

			if (pendingChanges.casterForeman !== undefined && processId) {
				promises.push(
					upsertProcessData(processId, "Foreman", pendingChanges.casterForeman),
				);
			}

			if (
				pendingChanges.melter !== undefined &&
				deliveryTicket.heatData.EAFProcessId
			) {
				promises.push(
					upsertEAFProcessData(
						deliveryTicket.heatData.EAFProcessId,
						"Foreman",
						pendingChanges.melter,
					),
				);
			}

			if (pendingChanges.labTech !== undefined) {
				promises.push(
					updateLabTechnician(
						deliveryTicket.heatData.HeatId,
						pendingChanges.labTech,
					),
				);
			}

			if (pendingChanges.sequence !== undefined && processId) {
				promises.push(
					upsertProcessData(processId, "Sequence", pendingChanges.sequence),
				);
			}

			const results = await Promise.all(promises);
			const errorsResp = results
				.filter((resp) => !resp.ok)
				.map((resp) => (!resp.ok ? resp.message : ""));

			if (errorsResp.length > 0) {
				setError(errorsResp.join(", "));
			} else {
				setPendingChanges({});
			}
		} catch (err) {
			setError("An unexpected error occurred while saving changes");
		}
	};

	const handleClickPrint = async () => {
		if (errors.size > 0) {
			setError("Please check all the fields");
			return;
		}
		let hasSectionError = false;
		let hasWeightError = false;

		deliveryTicket?.productBatches.forEach((batch) => {
			if (batch.SectionId === 0) {
				setErrors((prev) => {
					const newErrors = new Map(prev);
					newErrors.set(`Section-${batch.ProductBatchId}`, "Please select one");
					return newErrors;
				});
				hasSectionError = true;
			}

			if (!batch.Weight || batch.Weight <= 0) {
				setErrors((prev) => {
					const newErrors = new Map(prev);
					newErrors.set(
						`Weight-${batch.ProductBatchId}`,
						"Weight is required and must be greater than 0",
					);
					return newErrors;
				});
				hasWeightError = true;
			}
		});

		if (hasSectionError || hasWeightError) return;

		if (deliveryTicket && deliveryTicket.productBatches.length) {
			const totalPieces = deliveryTicket.productBatches.reduce(
				(acc, batch) => acc + batch.Pieces,
				0,
			);
			if (!totalPieces) {
				setError("Please add at least one piece of each");
				return;
			}
		}

		setIsLoading(true);
		await saveAllPendingChanges();

		if (deliveryTicket && deliveryTicket.productBatches && processId) {
			try {
				const promises = deliveryTicket.productBatches.map((batch) => {
					if (!processId) {
						setError("No process provided to print delivery ticket.");
						return;
					}
					return upsertProductBatch({
						productBatchId: batch.ProductBatchId,
						sectionId: batch.SectionId,
						processId: processId,
						weight: batch.Weight,
						pieces: batch.Pieces,
						length: batch.Length,
						isManualSaved: true,
					});
				});

				const results = await Promise.all(promises);
				const errorsResp = results
					.filter((resp) => !resp?.ok)
					.map((resp) => (resp && !resp.ok ? resp.message : ""));

				await loadData();

				if (errorsResp.length > 0) {
					setError(errorsResp.join(", "));
				} else {
					window.open(
						`/delivery-ticket/${processId}`,
						"_blank",
						"noopener,noreferrer",
					);
				}
			} catch (err) {
				setError("An unexpected error occurred");
			}
		}
		setIsLoading(false);
	};

	return {
		isLoading,
		error,
		deliveryTicket,
		moldSizes,
		pendingChanges,
		errors,
		dateTimeFormatted,
		handleHeatInputChange,
		handleProductBatchChange,
		handleClickPrint,
		refreshProductBatches,
		loadData,
		setError,
		setErrors,
		setPendingChanges,
	};
};
